// PaymentPage.js
import React, { useState, useEffect } from 'react';
import { useAuth } from '../../AuthContext';
import { useNavigate } from 'react-router-dom';
import { getDatabase, ref, update, onValue } from 'firebase/database';
import shared from '../../styles/shared.module.css';
import styles from './Payment.module.css';

const PaymentStatus = {
  COMPLETED: 'completed',
  PENDING: 'pending',
  UNPAID: 'unpaid'
};

const PaymentStatusCard = ({ status, completedAt, requestedAt }) => {
  const getStatusStyle = () => {
    switch (status) {
      case PaymentStatus.COMPLETED:
        return styles.statusCardCompleted;
      case PaymentStatus.PENDING:
        return styles.statusCardPending;
      default:
        return styles.statusCardUnpaid;
    }
  };

  const getStatusTitle = () => {
    switch (status) {
      case PaymentStatus.COMPLETED:
        return {
          text: '支払い済み',
          className: styles.statusTitleCompleted
        };
      case PaymentStatus.PENDING:
        return {
          text: '支払い確認中',
          className: styles.statusTitlePending
        };
      default:
        return {
          text: '未支払い',
          className: styles.statusTitleUnpaid
        };
    }
  };

  const getStatusText = () => {
    switch (status) {
      case PaymentStatus.COMPLETED:
        return {
          text: completedAt ? 
            `支払い完了日時: ${new Date(completedAt).toLocaleString('ja-JP')}` : 
            '支払いが完了しています。お飲み物をお楽しみください。',
          className: styles.statusTextCompleted
        };
      case PaymentStatus.PENDING:
        return {
          text: requestedAt ? 
            `リクエスト日時: ${new Date(requestedAt).toLocaleString('ja-JP')}` : 
            '支払いリクエストを受け付けました。スタッフが確認中です。',
          className: styles.statusTextPending
        };
      default:
        return {
          className: styles.statusTextUnpaid
        };
    }
  };

  const statusTitle = getStatusTitle();
  const statusText = getStatusText();

  return (
    <div className={`${styles.statusCard} ${getStatusStyle()}`}>
      <p className={`${styles.statusTitle} ${statusTitle.className}`}>
        {statusTitle.text}
      </p>
      <p className={`${styles.statusText} ${statusText.className}`}>
        {statusText.text}
      </p>
    </div>
  );
};

const PaymentPage = () => {
  const { currentUser, getUserProfile } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [userProfile, setUserProfile] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [paymentSettings, setPaymentSettings] = useState({
    paylink: '',
    payvalue: 0
  });

  // 支払い設定を取得
  useEffect(() => {
    const db = getDatabase();
    const settingsRef = ref(db, 'settings/payment');

    const unsubscribe = onValue(settingsRef, (snapshot) => {
      const data = snapshot.val();
      if (data) {
        setPaymentSettings({
          paylink: data.paylink || '',
          payvalue: data.payvalue || 0
        });
        setError(''); // エラーをクリア
      } else {
        // デフォルト値を設定
        setPaymentSettings({
          paylink: '',
          payvalue: 1000 // デフォルトの支払い金額
        });
      }
    }, (error) => {
      console.error('支払い設定の取得エラー:', error);
      // エラー時もデフォルト値を設定
      setPaymentSettings({
        paylink: '',
        payvalue: 1000
      });
      setError('支払い情報の取得に失敗しました。デフォルト設定を使用します。');
    });

    return () => unsubscribe();
  }, []);

  useEffect(() => {
    const fetchUserProfile = async () => {
      if (!currentUser) return;

      try {
        const profile = await getUserProfile(currentUser.uid);
        setUserProfile(profile);
      } catch (error) {
        console.error('プロフィール取得エラー:', error);
        setError('プロフィール情報の取得に失敗しました。');
      } finally {
        setLoading(false);
      }
    };

    fetchUserProfile();
  }, [currentUser, getUserProfile]);

  // 管理者が支払いを承認したときに即時「支払い済み」に切り替わるよう Main DB をリアルタイム購読
  useEffect(() => {
    if (!currentUser) return;
    const db = getDatabase();
    const userRef = ref(db, `users/${currentUser.uid}`);
    const unsubscribe = onValue(userRef, (snapshot) => {
      const data = snapshot.val();
      if (!data) return;
      setUserProfile((prev) => {
        if (!prev) return prev;
        return {
          ...prev,
          paymentStatus: data.paymentStatus ?? prev.paymentStatus,
          paymentCompletedAt: data.paymentCompletedAt ?? prev.paymentCompletedAt,
          paymentRequestedAt: data.paymentRequestedAt ?? prev.paymentRequestedAt
        };
      });
    });
    return () => unsubscribe();
  }, [currentUser]);

  const handlePaymentRequest = async () => {
    if (!currentUser) return;

    setIsProcessing(true);
    setError('');
    setSuccess('');

    try {
      const db = getDatabase();
      const userRef = ref(db, `users/${currentUser.uid}`);
      const timestamp = new Date().toISOString();

      // 料金が0円の場合は直接支払い済み状態にする
      if (paymentSettings.payvalue === 0) {
        console.log("料金が0円のため、直接支払い済み状態に設定します");
        
        await update(userRef, {
          paymentStatus: PaymentStatus.COMPLETED,
          paymentCompletedAt: timestamp,
          paymentAmount: paymentSettings.payvalue,
          paymentRequestedAt: timestamp // リクエスト日時も記録
        });

        setSuccess('料金が0円のため、支払いが自動的に完了しました。ドリンクを注文できます。');
        setUserProfile(prev => ({
          ...prev,
          paymentStatus: PaymentStatus.COMPLETED,
          paymentCompletedAt: timestamp,
          paymentRequestedAt: timestamp,
          paymentAmount: paymentSettings.payvalue
        }));
      } else {
        // 通常の支払いリクエスト処理
        await update(userRef, {
          paymentStatus: PaymentStatus.PENDING,
          paymentRequestedAt: timestamp,
          paymentAmount: paymentSettings.payvalue 
        });

        setSuccess('支払いリクエストを送信しました。スタッフが確認後に承認します。');
        setUserProfile(prev => ({
          ...prev,
          paymentStatus: PaymentStatus.PENDING,
          paymentRequestedAt: timestamp,
          paymentAmount: paymentSettings.payvalue
        }));
      }
    } catch (error) {
      console.error('支払いリクエストエラー:', error);
      setError('支払いリクエストの送信中にエラーが発生しました。');
    } finally {
      setIsProcessing(false);
    }
  };

  if (loading) {
    return (
      <div className={shared.loadingSpinner}>
        <div className={shared.spinner} />
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <header className={shared.pageHeader}>
        <div className={shared.headerContainer}>
          <h1 className={shared.headerTitle}>BANNAI'S BAR - 支払い</h1>
          <button
            onClick={() => navigate('/dashboard')}
            className={`${shared.backButton} ${styles.backButton}`}
          >
            戻る
          </button>
        </div>
      </header>

      <main className={styles.mainContent}>
        <div className={shared.card}>
          <h2 className={styles.cardTitle}>お支払い</h2>

          {error && (
            <div className={shared.errorMessage}>{error}</div>
          )}

          {success && (
            <div className={shared.successMessage}>{success}</div>
          )}

          <PaymentStatusCard
            status={userProfile?.paymentStatus}
            completedAt={userProfile?.paymentCompletedAt}
            requestedAt={userProfile?.paymentRequestedAt}
          />

          <div className={styles.paymentInfo}>
            <p className={styles.paymentDescription}>
              サービスを利用するには、お支払いが必要です。<br />
              下記のいずれかの方法でお支払いください。
            </p>
            <p className={styles.paymentAmount}>¥{paymentSettings.payvalue.toLocaleString()}</p>
            <p className={styles.paymentNote}>
              {paymentSettings.payvalue === 0 ? 
                '（入社おめでとう）' : 
                '（上記料金で飲み放題）'}
            </p>
          </div>

          <div className={styles.buttonContainer}>
            {userProfile?.paymentStatus !== PaymentStatus.COMPLETED && (
              <div className={styles.buttonRow}>
                {paymentSettings.paylink && (
                  <a
                    href={paymentSettings.paylink}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={`${styles.paymentButton} ${styles.paypayButton}`}
                  >
                    <svg
                      className={styles.paypayIcon}
                      viewBox="0 0 24 24"
                      fill="currentColor"
                    >
                      <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-1 14h2v2h-2v-2zm3.17-8.85c-.14-.32-.34-.61-.6-.86s-.57-.44-.92-.57a3.32 3.32 0 00-1.15-.19c-.44 0-.85.08-1.23.23-.38.15-.71.37-.99.64s-.49.6-.65.97c-.16.37-.24.77-.24 1.21 0 .26.02.51.07.74.05.23.11.44.2.64.09.2.19.37.31.53.12.16.25.3.39.42l1.29 1.19v1.72h2v-2.71l-1.29-1.19c-.14-.12-.24-.26-.31-.41-.07-.15-.11-.32-.11-.5 0-.31.09-.56.26-.77.17-.21.4-.31.7-.31.29 0 .53.1.71.29.18.19.27.44.27.74h2c0-.44-.08-.85-.24-1.21-.16-.37-.38-.69-.66-.96z"/>
                    </svg>
                    {paymentSettings.payvalue === 0 ? 'PayPayで応援' : 'PayPayで支払う'}
                  </a>
                )}
                <button
                  onClick={handlePaymentRequest}
                  disabled={isProcessing || userProfile?.paymentStatus === PaymentStatus.PENDING}
                  className={shared.primaryButton}
                >
                  {isProcessing ? '処理中...' : 
                    userProfile?.paymentStatus === PaymentStatus.PENDING ? '確認待ち中...' : 
                    paymentSettings.payvalue === 0 ? '出世払いを利用する' : '支払い確認をリクエスト'}
                </button>
              </div>
            )}
          </div>
        </div>
      </main>
    </div>
  );
};

export default PaymentPage;